import { documentToPlainTextString } from '@contentful/rich-text-plain-text-renderer'
import {
  IBlogHeaderModuleFields,
  IBlogTagModuleFields,
  IImageModuleFields,
  IPageModuleFields,
  ISeoTextModuleFields,
  ITaggedArticlesModuleFields,
} from 'contentful/types/contentful-modules'
import { MappingContext } from '@/components/content/contentMapper'
import { BlogTag } from '@/components/BlogTags/mapBlogTag'
import { BlogTaggedArticle } from '@/components/tagged-articles/tagged-articles.module'

const WORD_PER_MINUTE = 240

export const getBlogData = (
  entries: IPageModuleFields,
): { timeToRead: number; blogTags: BlogTag[]; title: string | undefined } => {
  let timeToRead = ''
  const blogTags: BlogTag[] = []
  let title: string | undefined

  entries?.content?.forEach(({ fields, sys }) => {
    switch (sys.contentType?.sys?.id) {
      case 'text.module': {
        timeToRead += documentToPlainTextString(
          (fields as ISeoTextModuleFields).text,
        )
        break
      }
      case 'blog-tag.module': {
        const { blogTag, slug } = (fields as IBlogTagModuleFields) || {}
        if (slug) {
          blogTags.push({ tagName: blogTag, tagSlug: `/${slug}` })
        }
        break
      }
      case 'blog-header.module': {
        title = (fields as IBlogHeaderModuleFields)?.title
        break
      }
    }
  })

  const words = timeToRead.trim().split(/\s+/).length
  const readingTime = Math.round(words / WORD_PER_MINUTE)

  return { timeToRead: readingTime, blogTags, title }
}

export const getTagsFromTaggedArticles = (
  entries: IPageModuleFields,
): BlogTag[] => {
  const blogContent = entries?.content
    ?.map(({ fields, sys }) => {
      if (sys.contentType.sys.id === 'tagged-articles.module') {
        const { tag } = fields as ITaggedArticlesModuleFields
        if (tag?.fields?.blogTag) {
          return {
            tagName: tag.fields.blogTag,
            tagSlug: tag.fields?.slug,
          }
        }
      }
    })
    .filter(Boolean)

  return blogContent
}

export const getBlogContent = (
  fields: IPageModuleFields,
  pageSlug?: string,
): BlogTaggedArticle => {
  const data: BlogTaggedArticle = {
    tags: [],
    title: '',
    description: '',
    imageUrl: '',
    imageAltText: '',
    link: fields?.slug,
  }

  fields.content?.forEach(({ sys, fields }) => {
    switch (sys?.contentType?.sys?.id) {
      case 'blog-header.module': {
        const { description, title } = (fields as IBlogHeaderModuleFields) || {}
        data.title = title
        data.description = description
        break
      }
      case 'image.module':
        if (!data.imageUrl) {
          data.imageUrl = (
            fields as IImageModuleFields
          ).image?.fields.defaultImage?.fields?.file?.url
          data.imageAltText = (
            fields as IImageModuleFields
          ).image?.fields.description
        }
        break
      case 'blog-tag.module': {
        const { blogTag, slug } = (fields as IBlogTagModuleFields) || {}
        if (slug) {
          data.tags.push({
            tagName: blogTag,
            tagSlug: pageSlug === slug ? undefined : `/${slug}`,
          })
        }
        break
      }
    }
  })
  return data
}

export const getTaggedArticles = async ({
  mappingContext,
  tagId,
  limit = 1000,
}: {
  mappingContext: MappingContext
  tagId?: string
  limit?: number
}): Promise<BlogTaggedArticle[] | []> => {
  const {
    ioc: { contentfulClient },
    brand,
    content,
    contentfulLocale,
  } = mappingContext

  const tagRelatedArticles =
    await contentfulClient.getEntries<IPageModuleFields>({
      content_type: 'page.module',
      ...(tagId && { 'fields.content.sys.id[in]': tagId }),
      'metadata.tags.sys.id[in]': brand,
      'fields.pageType': 'Blog',
      locale: contentfulLocale,
      limit,
      order: '-sys.createdAt',
      include: 3,
    })

  return (
    tagRelatedArticles.items?.map(({ fields }) =>
      getBlogContent(fields, content.slug),
    ) || []
  )
}

export const getAuthorName = (author: string): string => {
  //Author name is in the format of "firstName lastName email"
  const [firstName, lastName] = author?.split(' ') || ['']
  return `${firstName} ${lastName?.substring(0, 1) || ''}.`
}
